'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export type EstimatedLocation = {
    latitude: number;
    longitude: number;
};

let cachedLocation: EstimatedLocation | null = null;

/**
 * @internal — test use only.
 *
 * Jest reuses the same module instance across all tests in a file, so
 * `cachedLocation` keeps its value between tests. Call this in `afterEach`
 * to reset it so each test starts fresh and doesn't accidentally rely on
 * what a previous test set.
 */
export const __resetCachedLocationForTesting = () => {
    cachedLocation = null;
};

/**
 * Silently resolves browser geolocation coordinates when the user has already
 * granted permission.  Never prompts the user — if permission is "prompt" or
 * "denied" it returns null and the BFF falls back to IP-based lookup.
 *
 * Coordinates are cached in a module-level variable so every hook instance
 * (CartProvider, useFulfillmentMethod, etc.) shares the same value without
 * re-querying the browser.
 */
export const useEstimatedLocation = () => {
    const [location, setLocation] = useState<EstimatedLocation | null>(cachedLocation);
    const resolved = useRef(false);

    useEffect(() => {
        if (resolved.current || cachedLocation) return;
        resolved.current = true;

        if (typeof navigator === 'undefined' || !navigator.permissions || !navigator.geolocation) return;

        navigator.permissions
            .query({ name: 'geolocation' }) // NOSONAR — only reads when permission is already granted; never prompts
            .then(result => {
                if (result.state !== 'granted') return;

                // biome-ignore format: keeps NOSONAR on the same line so SonarQube suppresses S5604
                navigator.geolocation.getCurrentPosition( // NOSONAR
                    position => {
                        const loc: EstimatedLocation = {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        };
                        cachedLocation = loc;
                        setLocation(loc);
                    },
                    () => {
                        /* permission was granted but position unavailable — fall back to IP */
                    }
                );
            })
            .catch(() => {
                /* Permissions API not supported — fall back to IP */
            });
    }, []);

    const getLocation = useCallback(() => cachedLocation, []);

    return { estimatedLocation: location, getEstimatedLocation: getLocation };
};
